import { Platform } from 'react-native';
import { HomeReview, ReviewContactDetails } from '../types';

export type ReviewCreationServiceDependencies = {
  offlineReviewService: any;
  onlineApi: {
    createReview(
      propertyId: number,
      templateId: number,
      address?: string,
      details?: ReviewContactDetails
    ): Promise<HomeReview>;
  };
  getIsOnline: () => boolean;
  useOfflineWorkflow?: () => boolean;
  isWeb?: () => boolean;
};

export type ReviewCreateOptions = {
  forceOnline?: boolean;
  networkOnline?: boolean;
};

export class ReviewCreationService {
  constructor(private readonly deps: ReviewCreationServiceDependencies) {}

  async createReview(
    propertyId: number,
    templateId: number,
    address?: string,
    detailsOrOptions?: ReviewContactDetails | ReviewCreateOptions,
    options?: ReviewCreateOptions
  ): Promise<HomeReview> {
    const details = this.isCreateOptions(detailsOrOptions)
      ? undefined
      : detailsOrOptions;
    const resolvedOptions = this.isCreateOptions(detailsOrOptions)
      ? detailsOrOptions
      : options;

    try {
      const isOnline = resolvedOptions?.networkOnline ?? this.deps.getIsOnline();

      if (!resolvedOptions?.forceOnline && !isOnline) {
        return await this.createOfflineReview(propertyId, templateId, address, details);
      }

      const result = details
        ? await this.deps.onlineApi.createReview(propertyId, templateId, address, details)
        : await this.deps.onlineApi.createReview(propertyId, templateId, address);
      await this.cacheOnlineReview(result);
      return result;
    } catch (error) {
      if (resolvedOptions?.forceOnline || this.isWeb() || !this.isConnectivityError(error)) {
        throw error;
      }

      try {
        return await this.createOfflineReview(propertyId, templateId, address, details);
      } catch (offlineError) {
        throw error;
      }
    }
  }

  private isCreateOptions(
    value?: ReviewContactDetails | ReviewCreateOptions
  ): value is ReviewCreateOptions {
    return Boolean(value && ('forceOnline' in value || 'networkOnline' in value));
  }

  private isConnectivityError(error: unknown): boolean {
    const candidate = error as { message?: unknown; response?: unknown };

    if (candidate?.response) {
      return false;
    }

    const message = typeof candidate?.message === 'string'
      ? candidate.message.toLowerCase()
      : '';

    return message.includes('network request failed') ||
      message.includes('failed to fetch') ||
      message.includes('request timed out') ||
      message.includes('load failed');
  }

  private async createOfflineReview(
    propertyId: number,
    templateId: number,
    address?: string,
    details?: ReviewContactDetails
  ): Promise<HomeReview> {
    if (this.deps.offlineReviewService?.createOfflineReview) {
      return details
        ? await this.deps.offlineReviewService.createOfflineReview(
          propertyId,
          templateId,
          address,
          details
        )
        : await this.deps.offlineReviewService.createOfflineReview(
          propertyId,
          templateId,
          address
        );
    }

    throw new Error('Offline review service not available');
  }

  private async cacheOnlineReview(review: HomeReview): Promise<void> {
    if (!this.deps.offlineReviewService?.cacheOnlineReview) {
      return;
    }

    try {
      await this.deps.offlineReviewService.cacheOnlineReview(review);
    } catch (cacheError) {
      console.error('[ReviewService] Failed to cache new review offline:', cacheError);
    }
  }

  private isWeb(): boolean {
    return this.deps.isWeb ? this.deps.isWeb() : Platform.OS === 'web';
  }
}
